<template>
  <div>
    <div class="bg-grey-lighter p-3 mb-6 flex" v-if="order.delivery_type_id === 1 && (salesParticularsOrderItem || (windowCardOrderItem && !windowCardOrderItem.data.print) || (additionalWindowCardOrderItem && !additionalWindowCardOrderItem.data.print))">
      <i class="fas fa-info-circle mr-3"></i>
      <div>
        <strong class="block">Digital Delivery</strong>
        With digital delivery selected, your artwork orders will be available for download upon completion in the PDF format.
      </div>
    </div>

    <div v-if="salesParticularsPackItem">
      <div class="mb-3 flex">
        <div class="mr-12 flex-grow">
          <h3 class="mb-2 text-sm">Sales Particulars:</h3>
          <select v-model="salesParticularsOrderItem.data.id" class="border hover:border-grey-darker p-3 w-full">
            <option v-for="(option, i) in salesParticularsPackItem.options" :key="i" :value="i">
              {{ option.name }}{{ order.delivery_type_id === 0 ? ` x${ option.copies } copies` : '' }} {{ option.price ? `(${option.price >= 0 ? '+' : '-'}£${Math.abs(option.price)})` : ''}}
            </option>
          </select>
        </div>

        <div v-if="order.delivery_type_id === 0">
          <h3 class="mb-2 text-sm">Additional Copies:</h3>
          <select v-model="salesParticularsOrderItem.data.additional_copies_id" class="border hover:border-grey-darker p-3">
            <option :value="null">No additional copies</option>
            <option v-for="(option, i) in salesParticularsPackItem.options[salesParticularsOrderItem.data.id].additional_copies" :key="i" :value="i">
              +{{ option.quantity }} Copies ({{ option.price | gbp }})
            </option>
          </select>
        </div>
      </div>

      <div class="mb-6" v-if="locationMapsPackItem">
        <div v-if="locationMapsPackItem.included_in_pack" class="italic">
          Location Maps Included
        </div>
        <div v-else>
          <input type="checkbox" id="location_maps" v-model="locationMapsCheckbox" />
          <label for="location_maps" class="mr-12">
            Include Location Maps
            <span v-if="locationMapsPackItem.pivot.price != 0">({{ parseFloat(locationMapsPackItem.pivot.price) | gbp }})</span>
          </label>
        </div>
      </div>
      <div class="mb-3 flex"  v-if="locationMapsPackItem && locationMapsOrderItem">
        <div class="mr-12 flex-grow">
          <h3 class="mb-2 text-sm">Title Plan Reference:
            <div class="text-blue inline hover-trigger absolute">
                  <i class="fas fa-info-circle"></i>
                    <div class="hover-content bg-white border border-tertiary p-3 text-xs shadow text-left text-black font-normal whitespace-normal">
                      Please provide the ‘Title Plan Reference Number’ of the property, found at the top of the page of the Title Register. This allows us to accurately define the plot on the location map.
                    </div>
            </div>
          </h3>
          <input type="text" v-model="order.title_plan_reference" class="border hover:border-grey-darker p-3 w-full" />
        </div>
      </div>
      <div v-if="salesParticularsPackItem.fulfillment_by === 1" class="mb-8">
        <h3 class="mb-2 text-sm">Style:</h3>
        <div class="flex flex-wrap">
          <div class="w-full md:w-1/2 flex items-center mb-3" v-for="(style, i) in salesParticularsPackItem.options[salesParticularsOrderItem.data.id].styles" :key="i">
            <img v-if="style.images.length" :src="style.images[0]" class="w-32 mr-3 cursor-pointer" title="Preview" @click="previewStyle(style)" />
            <input type="radio" :id="`style${i}`" v-model="salesParticularsOrderItem.data.style_id" :value="i" />
            <label :for="`style${i}`" class="mr-12">{{ style.name }}</label>
          </div>
        </div>

        <modal
          width="70vw"
          v-if="templateStylePreview"
          @leave="templateStylePreview = null"
          show-close-btn
        >
          <h3 class="capitalize text-center mb-6">Sales Particulars Sample - {{ templateStylePreview.name }}</h3>
          <div class="flex items-center">
            <i class="fas fa-angle-left cursor-pointer fa-2x" v-if="templateStylePreviewIndex > 0 && templateStylePreview.images.length > 1" @click="templateStylePreviewIndex--" title="Previous"></i>
            <div class="mx-6">
              <img class="max-w-full shadow-md" :src="templateStylePreview.images[templateStylePreviewIndex]" />
            </div>
            <i class="fas fa-angle-right cursor-pointer fa-2x" v-if="templateStylePreviewIndex < templateStylePreview.images.length - 1 && templateStylePreview.images.length > 1" @click="templateStylePreviewIndex++" title="Next"></i>
          </div>
        </modal>
      </div>
      <div v-else-if="salesParticularsPackItem.fulfillment_by === 8" class="mb-8">
        <h3 class="mb-2 text-sm">Design:</h3>
        <marketing-pack-mmn-template-picker
          v-model="salesParticularsOrderItem.data.mmn_template_id"
          category="Property Brochures (Marketing Pack)"
          :genre="salesParticularsPackItem.options[salesParticularsOrderItem.data.id].mmn_subcategory"
          height="90" />
      </div>
    </div>

    <div v-if="windowCardPackItem">
      <hr class="border-b mb-6 border-grey-lighter" />

      <div class="mb-3">
        <input type="checkbox" id="window_card" v-model="windowCardCheckbox" />
        <label for="window_card" class="mr-12">
          Included Window Card
        </label>
      </div>

      <div class="flex items-center" v-if="windowCardOrderItem">
        <select v-model="windowCardOrderItem.data.id" class="border hover:border-grey-darker p-3 mr-12 flex-grow">
          <option :value="null">[Please Select Print Spec]</option>
          <option v-for="(option, i) in windowCardPackItem.options" :key="i" :value="i">
            {{ option.name }} {{ option[windowCardOrderItem.data.print ? 'price' : 'digital_price'] ? `(+£${option[windowCardOrderItem.data.print ? 'price' : 'digital_price'].toFixed(2)})` : '' }}
          </option>
        </select>

        <select v-model="windowCardOrderItem.data.print" class="border hover:border-grey-darker p-3 min-w-32">
          <option :value="true">Print Delivery</option>
          <option :value="false">Digital Delivery</option>
        </select>
      </div>

      <div v-if="windowCardPackItem.fulfillment_by === 8 && windowCardOrderItem && windowCardOrderItem.data.id !== null" class="my-6">
        <h3 class="mb-2 text-sm">Design:</h3>
        <marketing-pack-mmn-template-picker
          v-model="windowCardOrderItem.data.mmn_template_id"
          category="Window Card (Marketing Pack)"
          :genre="windowCardPackItem.options[windowCardOrderItem.data.id].mmn_subcategory"
          :height="windowCardPackItem.options[windowCardOrderItem.data.id].mmn_subcategory.indexOf('Portrait') >= 0 ? 180 : 90" />
      </div>
    </div>

    <div v-if="additionalWindowCardPackItem" class="mt-6">
      <div class="mb-3">
        <input type="checkbox" id="additional_window_card" v-model="additionalWindowCardCheckbox" />
        <label for="additional_window_card" class="mr-12">
          {{ windowCardPackItem ? 'Additional' : '' }} Window Cards
        </label>
      </div>

      <div v-if="additionalWindowCardOrderItem" class="flex">
        <select v-model="additionalWindowCardOrderItem.data.id" class="border hover:border-grey-darker p-3 mr-2 flex-grow w-64">
          <option :value="null">[Please Select Print Spec]</option>
          <option v-for="(option, i) in additionalWindowCardPackItem.options" :key="i" :value="i">
            {{ option.name }} {{ option[additionalWindowCardOrderItem.data.print ? 'price' : 'digital_price'] ? `(+£${option[additionalWindowCardOrderItem.data.print ? 'price' : 'digital_price'].toFixed(2)})` : '' }}
          </option>
        </select>
        <select v-model="additionalWindowCardOrderItem.data.print" class="border hover:border-grey-darker p-3 mr-2 w-24 min-w-32">
          <option :value="true">Print Delivery</option>
          <option :value="false">Digital Delivery</option>
        </select>
        <select v-model="additionalWindowCardOrderItem.data.copies" v-if="additionalWindowCardOrderItem.data.print" class="border hover:border-grey-darker p-3 w-16">
          <option :value=1 selected>Qty 1</option>
          <option :value=2>Qty 2</option>
          <option :value=3>Qty 3</option>
          <option :value=4>Qty 4</option>
          <option :value=5>Qty 5</option>
        </select>
      </div>

      <div v-if="additionalWindowCardPackItem.fulfillment_by === 8 && additionalWindowCardOrderItem && additionalWindowCardOrderItem.data.id !== null" class="my-6">
        <h3 class="mb-2 text-sm">Design:</h3>
        <marketing-pack-mmn-template-picker
          v-model="additionalWindowCardOrderItem.data.mmn_template_id"
          category="Window Card (Marketing Pack)"
          :genre="additionalWindowCardPackItem.options[additionalWindowCardOrderItem.data.id].mmn_subcategory"
          :height="additionalWindowCardPackItem.options[additionalWindowCardOrderItem.data.id].mmn_subcategory.indexOf('Portrait') >= 0 ? 180 : 90" />
      </div>
    </div>

    <div v-if="refinedPackItem && refinedPackItem.options.can_opt_out">
      <hr class="border-b my-6 border-grey-lighter" />
      <h3 class="mb-2 text-sm">Refined Magazine:</h3>
      <input type="checkbox" id="refined" v-model="refinedCheckbox" />
      <label for="refined" class="mr-12 leading-normal" style="white-space: normal !important;">
        Please tick here if this property does <span class="underline">not</span> require a Refined Magazine Feature page (e.g. the property is under discreet marketing). This will mean you also do not receive 10 printed copies of the Refined Magazine for this order.
      </label>

      <div v-if="refinedOrderItem && refinedPackItem.options.double_page_spread_locked_value === null" class="mt-3">
        <input type="checkbox" id="refineddb" v-model="refinedOrderItem.data.double_page_spread" />
        <label for="refineddb" class="mr-12 leading-normal" style="white-space: normal !important;">
          Double Page Spread Upgrade <span v-if="refinedPackItem.options.double_page_spread_cost">({{ refinedPackItem.options.double_page_spread_cost | gbp }})</span>
        </label>
      </div>
    </div>

    <div v-if="guildPrestigePackItem && guildPrestigePackItem.options.can_opt_out">
      <hr class="border-b my-6 border-grey-lighter" />
      <h3 class="mb-2 text-sm">Guild Magazine:</h3>
      <input type="checkbox" id="guildPrestige" v-model="guildPrestigeCheckbox" />
      <label for="guildPrestige" class="mr-12 leading-normal" style="white-space: normal !important;">
        Please tick here if this property does <span class="underline">not</span> require a page in The Guild Magazine. This will mean you also do not receive 10 printed copies of The Guild Magazine for this order.
      </label>

      <div v-if="guildPrestigeOrderItem">
        <h3 class="mb-2 text-sm mt-6">Design:</h3>
        <marketing-pack-mmn-template-picker
          v-model="guildPrestigeOrderItem.data.mmn_template_id"
          category="Guild Magazine (Marketing Pack)"
          genre="1x Properties per page"
          height="180" />
      </div>
    </div>
  </div>
</template>

<script>
import MarketingPackMmnTemplatePicker from '../../../components/MarketingPackMmnTemplatePicker.vue'
import Modal from '../../../components/Modal'
import { find, filter } from 'lodash'

export default {
  props: ['value', 'page', 'pack'],
  components: {
    Modal,
    MarketingPackMmnTemplatePicker
  },
  data () {
    return {
      order: this.value,
      templateStylePreview: null,
      templateStylePreviewIndex: 0
    }
  },
  computed: {
    salesParticularsPackItem () {
      return find(this.pack.items, (item) => item.internal_type_id === 3)
    },
    salesParticularsOrderItem () {
      return this.salesParticularsPackItem ? find(this.order.items, (item) => item.marketing_pack_item_id === this.salesParticularsPackItem.id) : null
    },
    windowCardPackItem () {
      return find(this.pack.items, (item) => item.internal_type_id === 4)
    },
    windowCardOrderItem () {
      return this.windowCardPackItem ? find(this.order.items, (item) => item.marketing_pack_item_id === this.windowCardPackItem.id) : null
    },
    additionalWindowCardPackItem () {
      return find(this.pack.items, (item) => item.internal_type_id === 8)
    },
    additionalWindowCardOrderItem () {
      return this.additionalWindowCardPackItem ? find(this.order.items, (item) => item.marketing_pack_item_id === this.additionalWindowCardPackItem.id) : null
    },
    refinedPackItem () {
      return find(this.pack.items, (item) => item.internal_type_id === 10)
    },
    refinedOrderItem () {
      return this.refinedPackItem ? find(this.order.items, (item) => item.marketing_pack_item_id === this.refinedPackItem.id) : null
    },
    locationMapsPackItem () {
      return find(this.pack.items, (item) => item.internal_type_id === 12)
    },
    locationMapsOrderItem () {
      return this.locationMapsPackItem ? find(this.order.items, (item) => item.marketing_pack_item_id === this.locationMapsPackItem.id) : null
    },
    guildPrestigePackItem () {
      return find(this.pack.items, (item) => item.internal_type_id === 20)
    },
    guildPrestigeOrderItem () {
      return this.guildPrestigePackItem ? find(this.order.items, (item) => item.marketing_pack_item_id === this.guildPrestigePackItem.id) : null
    },
    refinedCheckbox: {
      get () {
        return !this.refinedOrderItem
      },
      set (remove) {
        if (remove) {
          this.order.items.splice(this.order.items.indexOf(this.refinedOrderItem), 1)
          return
        }

        this.order.items.push({
          marketing_pack_item_id: this.refinedPackItem.id,
          delivery_address_id: null,
          data: {
            double_page_spread: false
          }
        })
      }
    },
    guildPrestigeCheckbox: {
      get () {
        return !this.guildPrestigeOrderItem
      },
      set (remove) {
        if (remove) {
          this.order.items.splice(this.order.items.indexOf(this.guildPrestigeOrderItem), 1)
          return
        }

        this.order.items.push({
          marketing_pack_item_id: this.guildPrestigePackItem.id,
          delivery_address_id: null,
          data: {
            mmn_template_id: null
          }
        })
      }
    },
    windowCardCheckbox: {
      get () {
        return !!this.windowCardOrderItem
      },
      set (add) {
        if (add) {
          this.order.items.push({
            marketing_pack_item_id: this.windowCardPackItem.id,
            delivery_address_id: null,
            data: {
              id: null,
              print: this.order.delivery_type_id === 0
            }
          })
          return
        }

        this.order.items.splice(this.order.items.indexOf(this.windowCardOrderItem), 1)
      }
    },
    additionalWindowCardCheckbox: {
      get () {
        return !!this.additionalWindowCardOrderItem
      },
      set (add) {
        if (add) {
          this.order.items.push({
            marketing_pack_item_id: this.additionalWindowCardPackItem.id,
            delivery_address_id: null,
            data: {
              id: null,
              print: this.order.delivery_type_id === 0,
              copies: 1
            }
          })
          return
        }

        this.order.items.splice(this.order.items.indexOf(this.additionalWindowCardOrderItem), 1)
      }
    },
    locationMapsCheckbox: {
      get () {
        return !!this.locationMapsOrderItem
      },
      set (add) {
        if (add) {
          this.order.items.push({
            marketing_pack_item_id: this.locationMapsPackItem.id,
            delivery_address_id: null,
            data: null
          })
          return
        }

        this.order.items.splice(this.order.items.indexOf(this.locationMapsOrderItem), 1)
      }
    }
  },
  methods: {
    previewStyle (style) {
      this.templateStylePreviewIndex = 0
      this.templateStylePreview = style
    }
  },
  watch: {
    order: {
      handler () {
        this.$emit('input', this.order)
      },
      deep: true
    },
    'salesParticularsOrderItem.data.id': {
      handler () {
        let freeOptions = filter(this.salesParticularsPackItem.options[this.salesParticularsOrderItem.data.id].additional_copies, (option) => option.price === 0)
        let index = freeOptions.length === 0 ? null : freeOptions.length - 1

        this.salesParticularsOrderItem.data.additional_copies_id = index
        this.salesParticularsOrderItem.data.style_id = 0
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.hover-trigger {
  .hover-content {
    position: absolute;
    top: 100%;
    right: 0px;
    width: 200px;
    z-index: 2;
    display: none;
  }

  &:hover {
    .hover-content {
      display: block;
    }
  }
}
</style>
